(function() {
    'use strict';

    document.addEventListener('DOMContentLoaded', function() {

        if (customElements.get('endota-booking-modal')) return;

        customElements.define(
            'endota-booking-modal',
            class EndotaBookingModal extends DrawerComponent {

                /**
                 * Create a new booking modal instance.
                 */
                constructor() {
                    super();
                    this._currentServiceId = '';
                    this._currentProductIds = []; // NEW — fallback Zenoti Service IDs from the Zenoti Product List metafield, used when the treatment has no direct Zenoti Service ID (e.g. generic treatments like Waxing)
                    this._activeTreatmentId = '';
                    this._defaultSpaData = null;
                    this._searchController = null;
                    this._isSearchWired = false;
                    this._isCurrentLocationLoading = false;
                    this._isEventsBound = false;
                    this._activeTab = null;
                    this._nearMeSpas = null;
                    this._initialNearMeSpas = null;
                    this._selectedSpa = null;
                    this._redirectUrl = '';
                    this._isConfirmationStep = false;
                    this._isOpen = false;
                }

                /**
                 * When the modal attaches to the page, initialize config and bind UI events.
                 */
                connectedCallback() {
                    super.connectedCallback();
                    const config = window.spaFinderModalData || {};
                    this._zenotiBaseUrl = config.zenotiBaseUrl || '';
                    this._bookingUrl = config.bookingUrl || '';
                    this._apiBaseUrl = config.apiBaseUrl || '';
                    this._mapTilerApiBaseUrl = config.mapTilerApiBaseUrl || 'https://api.maptiler.com/geocoding/';
                    this._mapTilerKey = config.mapTilerKey || '';
                    this._defaultDistance = config.defaultDistance || 50;
                    this._paramName = config.paramName || '';
                    this._disableCta();
                    this._bindEvents();
                    this._wireSearch();
                    this._preventOutsideClose();
                }

                show() {
                    this._isOpen = true;
                    super.show();
                }

                hide() {
                    // Flip the open flag immediately so the outside-click guard
                    // stops intercepting clicks as soon as we start closing —
                    // not just after the transition finishes.
                    this._isOpen = false;
                    // Fire super.hide() first to start the close animation.
                    // Then reset state only after the transition finishes so the UI
                    // never flashes back to step 1 while the modal is still visible.
                    super.hide();
                    const onTransitionEnd = () => {
                        this.removeEventListener('transitionend', onTransitionEnd);
                        this._resetSearch();
                    };
                    this.addEventListener('transitionend', onTransitionEnd);
                    // Safety fallback: if no transition fires (e.g. reduced-motion), reset immediately
                    const fallback = setTimeout(() => {
                        this.removeEventListener('transitionend', onTransitionEnd);
                        this._resetSearch();
                    }, 400);
                    // Cancel fallback if transition fires first
                    this.addEventListener('transitionend', () => clearTimeout(fallback), {
                        once: true
                    });
                }

                /**
                 * Get the effective service ID for the current booking flow.
                 * @returns {string}
                 */
                _getEffectiveId() {
                    return this._currentServiceId || '';
                }

                /**
                 * NEW — Get the Zenoti Service ID(s) to use for spa lookups, in priority order:
                 * 1. The direct Zenoti Service ID on the treatment (current behaviour, e.g. non-generic treatments)
                 * 2. Fallback list of Service IDs resolved from the Zenoti Product List metafield
                 *    (generic treatments, e.g. Waxing, that don't have their own Zenoti Service ID)
                 * Returns an array so callers can fan out lookups across multiple IDs and merge results.
                 * @returns {string[]}
                 */
                _getServiceIds() {
                    if (this._currentServiceId) return [this._currentServiceId];
                    if (Array.isArray(this._currentProductIds) && this._currentProductIds.length) {
                        return this._currentProductIds;
                    }
                    return [];
                }

                /**
                 * Get the user location input element.
                 * @returns {HTMLInputElement|null}
                 */
                _getSearchInput() {
                    return this.querySelector('#spa-location-input');
                }

                /**
                 * Get the clear search button.
                 * @returns {HTMLElement|null}
                 */
                _getClearButton() {
                    return this.querySelector('#spa-clear-text');
                }

                /**
                 * Get the current location button.
                 * @returns {HTMLElement|null}
                 */
                _getCurrentLocButton() {
                    return this.querySelector('#spa-current-location');
                }

                /**
                 * Get the spa search form.
                 * @returns {HTMLFormElement|null}
                 */
                _getSearchForm() {
                    return this.querySelector('#spa-search-form');
                }

                /**
                 * Get the search suggestion dropdown.
                 * @returns {HTMLElement|null}
                 */
                _getSearchDropdown() {
                    return this.querySelector('#spa-search-dropdown');
                }

                /**
                 * Get the spa tabs navigation element.
                 * @returns {HTMLElement|null}
                 */
                _getTabsNav() {
                    return this.querySelector('#spa-tabs-nav');
                }

                /**
                 * Get the container for tab panels.
                 * @returns {HTMLElement|null}
                 */
                _getTabsPanels() {
                    return this.querySelector('#spa-tabs-panels');
                }

                /**
                 * Get the booking CTA anchor.
                 * @returns {HTMLAnchorElement|null}
                 */
                _getBookingCta() {
                    return this.querySelector('#booking-modal-book-cta');
                }

                /**
                 * Get the default selection view.
                 * @returns {HTMLElement|null}
                 */
                _getDefaultState() {
                    return this.querySelector('.booking-modal-default-state');
                }

                /**
                 * Get the redirect confirmation view.
                 * @returns {HTMLElement|null}
                 */
                _getRedirectState() {
                    return this.querySelector('.booking-modal-redirect-state');
                }

                /**
                 * Get the treatment description element.
                 * @returns {HTMLElement|null}
                 */
                _getTreatmentDescEl() {
                    return this.querySelector('#booking-modal-treatment-description');
                }

                /**
                 * Get the treatment image wrapper.
                 * @returns {HTMLElement|null}
                 */
                _getTreatmentImageWrap() {
                    return this.querySelector('#booking-modal-treatment-image-wrap');
                }

                /**
                 * Get the treatment image element.
                 * @returns {HTMLImageElement|null}
                 */
                _getTreatmentImage() {
                    return this.querySelector('#booking-modal-treatment-image');
                }

                /**
                 * Get the desktop selected-spa panel (right column).
                 * @returns {HTMLElement|null}
                 */
                _getSelectedSpaPanel() {
                    return this.querySelector('#booking-modal-selected-spa');
                }

                /**
                 * Get the mobile selected-spa summary (footer, next to the CTA).
                 * @returns {HTMLElement|null}
                 */
                _getSelectedSpaPanelMobile() {
                    return this.querySelector('#booking-modal-selected-spa-mobile');
                }

                /** Set Clear Button Visibility. */
                _setClearButtonVisibility(clearBtn, input) {
                    if (!clearBtn || !input) return;
                    const hasValue = input.value.trim().length > 0;
                    clearBtn.hidden = !hasValue;
                    clearBtn.style.display = hasValue ? 'block' : 'none';
                }

                /** Show Redirect State. */
                _showRedirectState(spaName) {
                    const defaultState = this._getDefaultState();
                    const redirectState = this._getRedirectState();
                    const descEl = this._getTreatmentDescEl();
                    if (defaultState) defaultState.hidden = true;
                    if (descEl) descEl.hidden = true;
                    if (redirectState) {
                        const bodyEl = redirectState.querySelector('.booking-modal-redirect-state__body');
                        if (bodyEl) {
                            if (!bodyEl.dataset.originalText) bodyEl.dataset.originalText = bodyEl.innerHTML;
                            bodyEl.innerHTML = bodyEl.dataset.originalText.replace('[selected spa]', spaName || 'selected spa');
                        }
                        redirectState.hidden = false;
                    }
                }

                /**
                 * The base DrawerComponent closes the modal when the overlay /
                 * outside area is clicked. We only want THIS modal instance to
                 * close via the explicit close button, so intercept and block
                 * any click that isn't the close button — but ONLY while this
                 * particular modal is open, and only for clicks that belong to
                 * this modal's own DOM. This keeps the behaviour scoped to this
                 * modal and stops it from leaking into other drawers/modals on
                 * the page (which previously broke because we were checking the
                 * shared `body.modal-show` class instead of this instance's own
                 * open state).
                 */
                _preventOutsideClose() {
                    document.addEventListener('click', (e) => {
                        if (!this._isOpen) return;

                        const isCloseBtn = e.target.closest('.drawer__close-btn, .booking-modal-close-btn');
                        if (isCloseBtn) return; // allow normal close

                        const clickedInsideDrawerContent = e.target.closest('.drawer__inner');
                        if (clickedInsideDrawerContent) return; // clicks inside the modal content are fine

                        // Only intervene for clicks that belong to THIS modal's own
                        // element tree (e.g. its own overlay). Clicks anywhere else
                        // on the page — including other modals/drawers — are left
                        // alone so their own close behaviour keeps working normally.
                        if (!this.contains(e.target)) return;

                        // Anything else (this modal's own overlay, outside the drawer
                        // content) while open: block it from reaching the base
                        // class's close handler.
                        e.stopImmediatePropagation();
                        e.preventDefault();
                    }, true); // capture phase so this runs before the base class handler
                }

                /** Show Default State. */
                _showDefaultState() {
                    const defaultState = this._getDefaultState();
                    const redirectState = this._getRedirectState();
                    const descEl = this._getTreatmentDescEl();
                    if (defaultState) defaultState.hidden = false;
                    if (redirectState) redirectState.hidden = true;
                    if (descEl) descEl.hidden = false;
                }

                /** Reset Search. */
                _resetSearch() {
                    const input = this._getSearchInput();
                    const clearBtn = this._getClearButton();
                    const currentLocBtn = this._getCurrentLocButton();
                    if (input) input.value = '';
                    this._setClearButtonVisibility(clearBtn, input);
                    if (currentLocBtn) {
                        currentLocBtn.style.display = 'none';
                    }
                    const dropdown = this._getSearchDropdown();
                    if (dropdown) dropdown.hidden = true;
                    this._abortSearch();
                    this._showDefaultState();
                    if (this._defaultSpaData) {
                        this._renderSpaResults(this._defaultSpaData);
                    }
                    this._disableCta('Continue');
                    this._clearSelectedSpa();
                    // RESET
                    this._selectedSpa = null;
                    this._redirectUrl = '';
                    this._isConfirmationStep = false;
                }

                /** Clear Selected Spa. */
                _clearSelectedSpa() {
                    this.querySelectorAll('.spa-item.is-selected')
                        .forEach((el) => {
                            el.classList.remove('is-selected');
                        });
                    const spaPanel = this._getSelectedSpaPanel();
                    const spaPanelMobile = this._getSelectedSpaPanelMobile();
                    if (spaPanel) {
                        spaPanel.hidden = true;
                    }
                    if (spaPanelMobile) {
                        spaPanelMobile.hidden = true;
                    }
                    this._showDefaultState();
                    this.classList.remove('spa-selected'); // NEW — hides the mobile footer bar again
                }

                /** Abort Search. */
                _abortSearch() {
                    if (this._searchController) {
                        this._searchController.abort();
                        this._searchController = null;
                    }
                }
                _getRedirectUrlWithReferral() {
                    if (!this._redirectUrl) return '';

                    try {
                        const raw = localStorage.getItem('endota_booking_referral_code');
                        if (!raw) return this._redirectUrl;

                        const {
                            value,
                            expiry
                        } = JSON.parse(raw);

                        if (!value || Date.now() > expiry) {
                            localStorage.removeItem('endota_booking_referral_code');
                            return this._redirectUrl;
                        }

                        const url = new URL(this._redirectUrl);
                        url.searchParams.set(this._paramName, value);

                        return url.toString();
                    } catch (e) {
                        return this._redirectUrl;
                    }
                }
                /**
                 * Shared loading-spinner markup, reused for "Loading spas..." and
                 * "Getting your location..." states so every async wait in the
                 * modal looks the same (re-uses the redirect-state spinner styling).
                 */
                _spinnerHtml(message = '') {
                    return `
            <div class="spa-loading-state">
              <svg
                class="booking-modal-redirect-state__spinner"
                width="40"
                height="40"
                viewBox="0 0 56 56"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="28" cy="28" r="23" stroke="#2B6229" stroke-width="3" stroke-dasharray="100 44" stroke-linecap="round"/>
              </svg>
              ${message ? `<p class="spa-tabs-message">${this._escapeHtml(message)}</p>` : ''}
            </div>
          `;
                }

                /** Show Loading Spinner. */
                _showLoadingSpinner(message) {
                    const nav = this._getTabsNav();
                    const panels = this._getTabsPanels();
                    const legacy = this.querySelector('.booking-modal-spa-results');
                    if (nav) nav.innerHTML = '';
                    if (panels) panels.innerHTML = this._spinnerHtml(message);
                    else if (legacy) legacy.innerHTML = this._spinnerHtml(message);
                }

                /** Bind Events. */
                _bindEvents() {
                    if (this._isEventsBound) return;
                    this._isEventsBound = true;
                    document.addEventListener('click', (e) => {
                        const btn = e.target.closest('.quick-booking-btn, .book-now-btn');
                        if (!btn) return;
                        e.preventDefault();
                        e.stopPropagation();
                        const data = this._getTreatmentData(btn);
                        this._currentServiceId = data.serviceId;
                        this._currentProductIds = data.productIds; // NEW — fallback IDs for generic treatments with no direct Zenoti Service ID
                        this._activeTreatmentId = data.treatmentId;
                        this._injectTreatment(data.name, data.duration, data.description, data.image);
                        this._selectedSpa = null;
                        this._redirectUrl = '';
                        this._isConfirmationStep = false;
                        this._disableCta('Continue');
                        this._clearSelectedSpa();
                        this.show();
                        this._fetchSpasByTreatment();
                    }, true);
                    const cta = this._getBookingCta();
                    if (cta) {
                        cta.addEventListener('click', (e) => {
                            e.preventDefault();
                            // disabled
                            if (cta.classList.contains('is-disabled')) {
                                return;
                            }
                            // FIRST CLICK = Continue
                            if (!this._isConfirmationStep) {
                                this._isConfirmationStep = true;
                                if (this._selectedSpa) {
                                    this._updateSelectedSpaPanel(
                                        this._selectedSpa
                                    );
                                    this._showRedirectState(
                                        this._selectedSpa.displayName || ''
                                    );
                                }
                                cta.textContent = 'Book now';
                                // AUTO REDIRECT AFTER 3 SECONDS
                                setTimeout(() => {
                                    if (this._redirectUrl) {
                                        window.location.href = this._getRedirectUrlWithReferral();
                                    }
                                }, 3000);
                                return;
                            }
                            // SECOND CLICK = Redirect
                            if (this._redirectUrl) {
                                // show loading state immediately
                                const cta = this._getBookingCta();
                                if (cta) {
                                    cta.classList.add('is-loading');
                                    cta.textContent = 'Redirecting...';
                                }
                                window.location.href = this._getRedirectUrlWithReferral();
                            }
                        });
                    }
                }

                /** Wire Search. */
                _wireSearch() {
                    if (this._isSearchWired) return;
                    this._isSearchWired = true;
                    const input = this._getSearchInput();
                    const form = this._getSearchForm();
                    const clearBtn = this._getClearButton();
                    const currentLocBtn = this._getCurrentLocButton();
                    const dropdown = this._getSearchDropdown();
                    if (!input || !form || !clearBtn) return;
                    if (currentLocBtn) currentLocBtn.style.display = 'none';
                    this._setClearButtonVisibility(clearBtn, input);
                    input.addEventListener('input', () => {
                        this._setClearButtonVisibility(clearBtn, input);
                        this._disableCta();
                        this._clearSelectedSpa();
                    });

                    input.addEventListener('focus', () => {
                        this._setClearButtonVisibility(clearBtn, input);
                        const query = input.value.trim();

                        if (currentLocBtn) currentLocBtn.style.display = 'none';
                        if (query) {
                            this._showSearchDropdownLoading();
                            this._fetchSpaSuggestions(query);
                        } else {
                            this._showSearchDropdown({}, true);
                        }
                    });


                    input.addEventListener('keydown', (event) => {
                        if (event.key !== 'Enter') return;
                        event.preventDefault();
                        const query = input.value.trim();
                        if (!query) return;

                        dropdown.hidden = true;
                        this._clearSelectedSpa();
                        this._disableCta();
                        this._searchNearbyByLocationQuery(query);
                    });

                    input.addEventListener('keyup', (event) => {
                        if (event.key === 'Enter') return;

                        const query = input.value.trim();

                        if (!query) {
                            this._abortSearch();

                            // Restore NEAR ME data (same as Clear button)
                            this._nearMeSpas = this._initialNearMeSpas;
                            this._activeTab = 'NEAR ME';

                            this._showSearchDropdown({}, true);
                            this._renderSpaResults(this._defaultSpaData || {});
                            this._disableCta();
                            this._clearSelectedSpa();

                            if (dropdown) {
                                dropdown.innerHTML = '';
                                dropdown.hidden = true;
                            }

                            return;
                        }

                        this._showSearchDropdownLoading();
                        this._fetchSpaSuggestions(query);
                    });

                    form.addEventListener('submit', (e) => {
                        e.preventDefault();
                        const query = input.value.trim();
                        if (!query) return;

                        dropdown.hidden = true;
                        this._clearSelectedSpa();
                        this._disableCta();
                        this._searchNearbyByLocationQuery(query);
                    });

                    document.addEventListener('mousedown', (event) => {
                        if (!dropdown || dropdown.hidden) return;
                        if (event.target.closest('.spa-search-box')) return;
                        dropdown.hidden = true;
                    });

                    clearBtn.addEventListener('click', () => {
                        input.value = '';
                        this._setClearButtonVisibility(clearBtn, input);
                        this._abortSearch();

                        // Reset NEAR ME tab data
                        this._nearMeSpas = this._initialNearMeSpas;
                        this._activeTab = 'NEAR ME';

                        // Restore the default state
                        this._renderSpaResults(this._defaultSpaData || {});
                        this._disableCta();
                        this._clearSelectedSpa();
                        if (dropdown) {
                            dropdown.innerHTML = '';
                            dropdown.hidden = true;
                        }
                    });

                    if (currentLocBtn) {
                        currentLocBtn.addEventListener('click', (e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            currentLocBtn.style.display = 'none';
                            this._useCurrentLocation();
                        });
                    }

                }


                /** Get Treatment Data. */
                _getTreatmentData(btn) {
                    let image = btn.dataset.image || btn.dataset.featuredImage || btn.dataset.imageUrl || btn.dataset.productImage || btn.dataset.src || '';
                    if (!image) {
                        const cardSelectors = ['article', '[class*="card"]', '[class*="product"]', '[class*="treatment"]', '[class*="item"]', 'li', '.grid__item', '.collection-item'];
                        let card = null;
                        for (const sel of cardSelectors) {
                            card = btn.closest(sel);
                            if (card) break;
                        }
                        if (card) {
                            const mediaImg = card.querySelector('.card__media img, .card-media img, .product-media img, [class*="media"] img, [class*="image"] img, picture img, img');
                            if (mediaImg) image = mediaImg.currentSrc || mediaImg.src || mediaImg.dataset.src || mediaImg.dataset.lazySrc || '';
                        }
                    }
                    if (!image) {
                        let el = btn.parentElement;
                        while (el && el !== document.body) {
                            const img = el.querySelector('img');
                            if (img) {
                                image = img.currentSrc || img.src || img.dataset.src || '';
                                if (image) break;
                            }
                            el = el.parentElement;
                        }
                    }
                    if (image && (image.startsWith('data:image/svg') || image.includes('placeholder'))) image = '';

                    // NEW — parse the fallback Zenoti Product List service IDs
                    // (comma-separated string from data-product-list). Used only
                    // when this treatment has no direct Zenoti Service ID.
                    const productIds = (btn.dataset.productList || '')
                        .split(',')
                        .map((s) => s.trim())
                        .filter(Boolean);

                    return {
                        name: btn.dataset.serviceName || '',
                        duration: btn.dataset.duration || '',
                        description: btn.dataset.description || '',
                        treatmentId: btn.dataset.id || '',
                        serviceId: btn.dataset.serviceId || '',
                        productIds, // NEW
                        image
                    };
                }

                /** Inject Treatment. */
                _injectTreatment(name, duration, description, image) {
                    // Treatment name
                    const nameEl = this.querySelector('#booking-modal-treatment-name');
                    if (nameEl) nameEl.textContent = name;

                    // Duration — populate the text span inside the clock-icon row;
                    // hide the whole row if there's no duration value.
                    const durationRow = this.querySelector('#booking-modal-treatment-duration');
                    const durationText = this.querySelector('.booking-modal-duration-text');
                    if (durationText) durationText.textContent = duration;
                    if (durationRow) durationRow.style.display = duration ? '' : 'none';

                    // Description
                    const descEl = this.querySelector('#booking-modal-treatment-description');
                    if (descEl) descEl.textContent = description;

                    // Image
                    const imageWrap = this._getTreatmentImageWrap();
                    const imageEl = this._getTreatmentImage();
                    if (imageWrap && imageEl) {
                        if (image) {
                            imageEl.src = image;
                            imageEl.alt = name || 'Treatment image';
                            imageWrap.hidden = false;
                        } else {
                            imageWrap.hidden = true;
                            imageEl.src = '';
                            imageEl.alt = '';
                        }
                    }

                    // Ensure description is visible
                    if (descEl) descEl.hidden = false;
                }

                /** Disable Cta. */
                _disableCta(label = 'Continue') {
                    const cta = this._getBookingCta();
                    if (!cta) return;
                    cta.classList.add('is-disabled');
                    cta.removeAttribute('href');
                    cta.style.pointerEvents = 'none';
                    cta.style.cursor = 'default';
                    cta.textContent = label;
                }
                /** Enable Cta. */
                _enableCta(label = 'Continue') {
                    const cta = this._getBookingCta();
                    if (!cta) return;
                    cta.classList.remove('is-disabled');
                    cta.style.pointerEvents = 'auto';
                    cta.style.cursor = 'pointer';
                    cta.textContent = label;
                }

                /**
                 * Patch Cta Url.
                 * NOTE: serviceId is only appended when present. In the fallback
                 * (Zenoti Product List) flow this._currentServiceId is '', so no
                 * serviceid param is sent — the customer hasn't made a specific
                 * treatment selection yet, per the "generic treatment" requirement.
                 */
                _patchCtaUrl(centreId, serviceId) {
                    if (!centreId) return;
                    const base = this._zenotiBaseUrl.replace(/\/$/, '');
                    const path = this._bookingUrl.replace(
                        '[spa_id]',
                        encodeURIComponent(centreId)
                    );
                    let finalUrl = `${base}/${path.replace(/^\/+/, '')}`;
                    try {
                        const url = new URL(finalUrl);
                        if (serviceId) {
                            url.searchParams.set('serviceid', serviceId);
                        }
                        finalUrl = url.toString();
                    } catch (error) {
                        console.warn('Invalid booking URL', error);
                    }
                    this._redirectUrl = finalUrl;
                }

                /** Use Current Location. */
                _useCurrentLocation() {
                    const dropdown = this._getSearchDropdown();
                    if (dropdown) {
                        dropdown.hidden = true;
                    }

                    const nav = this._getTabsNav();
                    const panels = this._getTabsPanels();

                    if (!nav || !panels) return;

                    // Activate NEAR ME tab only
                    const nearMeBtn = nav.querySelector('[data-tab-key="NEAR ME"]');

                    if (nearMeBtn) {
                        // Only change active tab UI
                        nav.querySelectorAll('.spa-tab-btn').forEach(tab => {
                            tab.classList.remove('is-active');
                            tab.setAttribute('aria-selected', 'false');
                        });

                        nearMeBtn.classList.add('is-active');
                        nearMeBtn.setAttribute('aria-selected', 'true');

                        panels.querySelectorAll('.spa-tab-panel').forEach(panel => {
                            panel.classList.remove('is-active');
                            panel.hidden = true;
                        });

                        const nearMePanel = panels.querySelector('#spa-panel-NEAR-ME');
                        if (nearMePanel) {
                            nearMePanel.classList.add('is-active');
                            nearMePanel.hidden = false;
                        }

                        this._activeTab = 'NEAR ME';
                        this._clearSelectedSpa();
                        this._disableCta('Continue');
                        this._selectedSpa = null;
                        this._redirectUrl = '';
                        this._isConfirmationStep = false;
                    }
                }

                /**
                 * Fetch Spas By Treatment.
                 * UPDATED — now resolves against _getServiceIds() (one ID for a
                 * normal treatment, or multiple IDs from the Zenoti Product List
                 * fallback for generic treatments), fans the lookup out across all
                 * IDs, and merges the results into a single spasByState map.
                 */
                async _fetchSpasByTreatment() {
                    const ids = this._getServiceIds();
                    if (!ids.length) {
                        this._showTabsMessage('Failed to load spas.');
                        return;
                    }
                    this._showLoadingSpinner('Loading spas...');
                    this._nearMeSpas = null;
                    this._activeTab = 'NEAR ME';
                    try {
                        const results = await Promise.all(
                            ids.map((id) =>
                                fetch(`${this._apiBaseUrl}/api/treatments/${id}/spas/by-state`)
                                    .then((r) => r.json())
                                    .catch(() => null)
                            )
                        );

                        let mergedSpasByState = {};
                        let anySuccess = false;

                        results.forEach((data) => {
                            if (data && data.success && data.totalSpas > 0) {
                                anySuccess = true;
                                const filteredSpasByState = Object.fromEntries(
                                    Object.entries(data.spasByState)
                                        .map(([state, spas]) => [
                                            state,
                                            spas.filter(spa => spa.acceptsBooking)
                                        ])
                                        .filter(([, spas]) => spas.length)
                                );
                                mergedSpasByState = this._mergeSpasByState(mergedSpasByState, filteredSpasByState);
                            }
                        });

                        if (anySuccess && Object.keys(mergedSpasByState).length) {
                            this._defaultSpaData = mergedSpasByState;
                            this._renderSpaResults(mergedSpasByState);
                        } else {
                            this._defaultSpaData = {};
                            this._showNoTreatmentSpasMessage();
                            const nav = this._getTabsNav();
                            if (nav) {
                                nav.style.display = 'none';
                            }
                        }
                    } catch {
                        this._defaultSpaData = {};
                        this._showNoTreatmentSpasMessage();
                        const nav = this._getTabsNav();
                        if (nav) {
                            nav.style.display = 'none';
                        }
                    }
                }

                /**
                 * ── Shared "no results" empty state ──
                 * Used by: no treatment nearby, near-me empty, and no search results.
                 * Only the icon and copy differ between states — layout, styling,
                 * and button wiring (close modal / focus search input) live here once.
                 * @param {HTMLElement} container - element to render into
                 * @param {string} heading
                 * @param {string} subHtml - HTML string for the sub line (may include a button link)
                 * @param {string} [iconSvg] - raw SVG markup for the icon; falls back to the default icon
                 */
                _renderNoResultState(container, heading, subHtml, iconSvg) {
                    if (!container) return;
                    container.innerHTML = `
            <div class="spa-no-result">
              <div class="spa-no-result__icon">
                ${iconSvg || this._defaultNoResultIcon()}
              </div>
              <p class="spa-no-result__heading">${this._escapeHtml(heading)}</p>
              <p class="spa-no-result__sub">${subHtml}</p>
            </div>`;

                    // Wire "select a different treatment" → close the modal
                    const diffTreatmentBtn = container.querySelector('.select-diff-treatment-link');
                    if (diffTreatmentBtn) {
                        diffTreatmentBtn.addEventListener('click', () => this.hide());
                    }

                    // Wire "suburb, postcode, or address" → focus the search input
                    const searchLink = container.querySelector('.spa-focus-search-link');
                    if (searchLink) {
                        searchLink.addEventListener('click', () => {
                            const input = this._getSearchInput();
                            if (input) input.focus();
                        });
                    }
                }

                /** Default fallback icon used when no state-specific icon is supplied. */
                _defaultNoResultIcon() {
                    return `
            <svg width="27" height="27" viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.3333 26.6667C11.7111 26.4667 10.1 26.028 8.5 25.3507C6.9 24.6733 5.47245 23.7009 4.21734 22.4333C2.96222 21.1658 1.94533 19.5658 1.16667 17.6333C0.388002 15.7009 -0.000887369 13.3787 1.52033e-06 10.6667V9.33333H1.33333C2.46667 9.33333 3.63334 9.47778 4.83333 9.76667C6.03334 10.0556 7.15556 10.4889 8.2 11.0667C8.46667 9.15556 9.07244 7.19422 10.0173 5.18267C10.9622 3.17111 12.0676 1.44356 13.3333 0C14.6 1.44444 15.7058 3.17244 16.6507 5.184C17.5956 7.19556 18.2009 9.15644 18.4667 11.0667C19.5111 10.4889 20.6333 10.0556 21.8333 9.76667C23.0333 9.47778 24.2 9.33333 25.3333 9.33333H26.6667V10.6667C26.6667 13.3778 26.2778 15.7 25.5 17.6333C24.7222 19.5667 23.7058 21.1667 22.4507 22.4333C21.1956 23.7 19.7733 24.6724 18.184 25.3507C16.5947 26.0289 14.9778 26.4676 13.3333 26.6667ZM13.2667 23.9333C13.0222 20.2444 11.9276 17.4556 9.98267 15.5667C8.03778 13.6778 5.62134 12.5111 2.73334 12.0667C2.97778 15.8667 4.10578 18.7 6.11733 20.5667C8.12889 22.4333 10.512 23.5556 13.2667 23.9333ZM13.3333 15.4667C13.6667 14.9778 14.0724 14.4724 14.5507 13.9507C15.0289 13.4289 15.4898 12.9787 15.9333 12.6C15.8889 11.3333 15.6391 10.0111 15.184 8.63333C14.7289 7.25556 14.112 5.91111 13.3333 4.6C12.5556 5.91111 11.9387 7.25556 11.4827 8.63333C11.0267 10.0111 10.7769 11.3333 10.7333 12.6C11.1778 12.9778 11.6444 13.428 12.1333 13.9507C12.6222 14.4733 13.0222 14.9787 13.3333 15.4667ZM15.9333 23.3333C16.7556 23.0667 17.6111 22.6778 18.5 22.1667C19.3889 21.6556 20.2169 20.9613 20.984 20.084C21.7511 19.2067 22.4067 18.112 22.9507 16.8C23.4947 15.488 23.8222 13.9102 23.9333 12.0667C21.8444 12.3778 20.0111 13.0724 18.4333 14.1507C16.8556 15.2289 15.6444 16.6009 14.8 18.2667C15.0667 18.9778 15.2947 19.7556 15.484 20.6C15.6733 21.4444 15.8231 22.3556 15.9333 23.3333Z" fill="#34473A"/>
            </svg>`;
                }

                /** Icon: no treatment nearby. */
                _iconNoTreatment() {
                    return this._defaultNoResultIcon();
                }

                /** Icon: no nearby spas (NEAR ME empty). */
                _iconNoNearby() {
                    return `
                    <svg width="27" height="27" viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.3333 26.6667C11.7111 26.4667 10.1 26.028 8.5 25.3507C6.9 24.6733 5.47245 23.7009 4.21734 22.4333C2.96222 21.1658 1.94533 19.5658 1.16667 17.6333C0.388002 15.7009 -0.000887369 13.3787 1.52033e-06 10.6667V9.33333H1.33333C2.46667 9.33333 3.63334 9.47778 4.83333 9.76667C6.03334 10.0556 7.15556 10.4889 8.2 11.0667C8.46667 9.15556 9.07244 7.19422 10.0173 5.18267C10.9622 3.17111 12.0676 1.44356 13.3333 0C14.6 1.44444 15.7058 3.17244 16.6507 5.184C17.5956 7.19556 18.2009 9.15644 18.4667 11.0667C19.5111 10.4889 20.6333 10.0556 21.8333 9.76667C23.0333 9.47778 24.2 9.33333 25.3333 9.33333H26.6667V10.6667C26.6667 13.3778 26.2778 15.7 25.5 17.6333C24.7222 19.5667 23.7058 21.1667 22.4507 22.4333C21.1956 23.7 19.7733 24.6724 18.184 25.3507C16.5947 26.0289 14.9778 26.4676 13.3333 26.6667ZM13.2667 23.9333C13.0222 20.2444 11.9276 17.4556 9.98267 15.5667C8.03778 13.6778 5.62134 12.5111 2.73334 12.0667C2.97778 15.8667 4.10578 18.7 6.11733 20.5667C8.12889 22.4333 10.512 23.5556 13.2667 23.9333ZM13.3333 15.4667C13.6667 14.9778 14.0724 14.4724 14.5507 13.9507C15.0289 13.4289 15.4898 12.9787 15.9333 12.6C15.8889 11.3333 15.6391 10.0111 15.184 8.63333C14.7289 7.25556 14.112 5.91111 13.3333 4.6C12.5556 5.91111 11.9387 7.25556 11.4827 8.63333C11.0267 10.0111 10.7769 11.3333 10.7333 12.6C11.1778 12.9778 11.6444 13.428 12.1333 13.9507C12.6222 14.4733 13.0222 14.9787 13.3333 15.4667ZM15.9333 23.3333C16.7556 23.0667 17.6111 22.6778 18.5 22.1667C19.3889 21.6556 20.2169 20.9613 20.984 20.084C21.7511 19.2067 22.4067 18.112 22.9507 16.8C23.4947 15.488 23.8222 13.9102 23.9333 12.0667C21.8444 12.3778 20.0111 13.0724 18.4333 14.1507C16.8556 15.2289 15.6444 16.6009 14.8 18.2667C15.0667 18.9778 15.2947 19.7556 15.484 20.6C15.6733 21.4444 15.8231 22.3556 15.9333 23.3333Z" fill="#34473A"/>
                    </svg>`;
                }

                /** Icon: no search results for a location. */
                _iconNoSearchResults() {
                    return `
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16.6334 12.8L17.7334 13.9C17.9111 14.0778 18.1111 14.1058 18.3334 13.984C18.5556 13.8622 18.6667 13.6453 18.6667 13.3333C18.6667 12.9555 18.6 12.6053 18.4667 12.2827C18.3334 11.96 18.1445 11.6769 17.9 11.4333C17.6556 11.1667 17.3667 10.9724 17.0333 10.8507C16.7 10.7289 16.3556 10.6676 16 10.6667C15.7111 10.6667 15.5169 10.7947 15.4173 11.0507C15.3178 11.3067 15.3565 11.5231 15.5333 11.7L16.6334 12.8ZM16 2.66666C18.8222 2.66666 21.3058 3.65555 23.4507 5.63333C25.5956 7.61111 26.6676 10.2667 26.6667 13.6C26.6667 14.4 26.5502 15.2222 26.3174 16.0667C26.0845 16.9111 25.7342 17.7889 25.2667 18.7C25.0889 19.0333 24.8222 19.2444 24.4667 19.3333C24.1111 19.4222 23.7778 19.3778 23.4667 19.2C23.1556 19.0222 22.9498 18.7502 22.8493 18.384C22.7489 18.0178 22.788 17.6676 22.9667 17.3333C23.3 16.6444 23.5556 15.9942 23.7333 15.3827C23.9111 14.7711 24 14.1769 24 13.6C24 11.1778 23.228 9.19466 21.684 7.65066C20.14 6.10666 18.2454 5.33422 16 5.33333C15.2889 5.33333 14.5947 5.41688 13.9173 5.58399C13.24 5.75111 12.612 6.00088 12.0333 6.33333C11.7 6.51111 11.3498 6.58355 10.9827 6.55066C10.6156 6.51777 10.3102 6.35644 10.0667 6.06666C9.82313 5.77688 9.7289 5.44933 9.78402 5.08399C9.83913 4.71866 10.0222 4.44622 10.3334 4.26666C11.1778 3.75555 12.0836 3.36088 13.0507 3.08266C14.0178 2.80444 15.0009 2.66577 16 2.66666ZM19.0333 22.8L8.13335 11.9C8.0889 12.1667 8.05557 12.4444 8.03335 12.7333C8.01113 13.0222 8.00002 13.3111 8.00002 13.6C8.00002 15.1778 8.65557 16.9835 9.96668 19.0173C11.2778 21.0511 13.2889 23.312 16 25.8C16.5778 25.2889 17.1169 24.7836 17.6174 24.284C18.1178 23.7844 18.5898 23.2898 19.0333 22.8ZM15.0667 28.2667C14.7556 28.1556 14.4778 27.9889 14.2334 27.7667C12.7889 26.4333 11.5111 25.1333 10.4 23.8667C9.28891 22.6 8.36135 21.3724 7.61735 20.184C6.87335 18.9955 6.30668 17.8511 5.91735 16.7507C5.52802 15.6502 5.33335 14.6 5.33335 13.6C5.33335 12.8889 5.38891 12.2111 5.50002 11.5667C5.61113 10.9222 5.76668 10.3111 5.96668 9.73333L1.83335 5.59999C1.56668 5.33333 1.43335 5.01688 1.43335 4.65066C1.43335 4.28444 1.56668 3.96755 1.83335 3.69999C2.10002 3.43244 2.41691 3.29911 2.78402 3.29999C3.15113 3.30088 3.46757 3.43422 3.73335 3.69999L28.2667 28.2333C28.5334 28.5 28.6614 28.8111 28.6507 29.1667C28.64 29.5222 28.5009 29.8333 28.2333 30.1C27.9658 30.3667 27.6493 30.5 27.284 30.5C26.9187 30.5 26.6018 30.3667 26.3333 30.1L20.9 24.6667C20.5222 25.0667 20.1 25.5 19.6334 25.9667C19.1667 26.4333 18.5445 27.0333 17.7667 27.7667C17.5222 27.9889 17.2445 28.1556 16.9333 28.2667C16.6222 28.3778 16.3111 28.4333 16 28.4333C15.6889 28.4333 15.3778 28.3778 15.0667 28.2667Z" fill="#34473A"/>
                    </svg>`;
                }

                /** Show No Treatment Spas Message. */
                _showNoTreatmentSpasMessage() {
                    this._renderNoResultState(
                        this._getTabsPanels(),
                        'There are currently no nearby spa locations that offer this treatment',
                        'Find another spa or <button type="button" class="select-diff-treatment-link btn--link">select a different treatment</button>.',
                        this._iconNoTreatment()
                    );
                }

                /**
                 * Fetch Spa Suggestions.
                 * UPDATED — resolves against _getServiceIds() and fans the
                 * "suggest" lookup out across all IDs (single-ID treatments behave
                 * exactly as before), merging + de-duping the suggestion lists
                 * before continuing with the existing MapTiler / nearby logic.
                 */
                async _fetchSpaSuggestions(query) {
                    const ids = this._getServiceIds();
                    if (!ids.length) {
                        this._showTabsMessage('Failed to load spas.');
                        return;
                    }
                    this._abortSearch();
                    this._searchController = new AbortController();
                    const {
                        signal
                    } = this._searchController;
                    try {
                        const suggestUrls = ids.map(
                            (id) => `${this._apiBaseUrl}/api/treatments/${id}/spas/suggest?q=${encodeURIComponent(query)}`
                        );
                        const mapTilerUrl = `${this._mapTilerApiBaseUrl}${encodeURIComponent(query)}.json?key=${this._mapTilerKey}&types=region%2Cmunicipality%2Clocality%2Cneighbourhood%2Cpostal_code%2Caddress&country=au`;

                        const [suggestResults, mapTilerResult] = await Promise.all([
                            Promise.allSettled(
                                suggestUrls.map((url) => fetch(url, {
                                    signal
                                }).then((r) => r.json()))
                            ),
                            fetch(mapTilerUrl, {
                                signal
                            }).then((r) => r.json()).catch(() => null)
                        ]);

                        // If the user has clicked/focused away from the search input
                        // while this request was in flight, don't show the dropdown.
                        if (signal.aborted || document.activeElement !== this._getSearchInput()) return;

                        // Merge + de-dupe suggestions across all resolved service IDs.
                        let allSuggestions = [];
                        suggestResults.forEach((result) => {
                            if (result.status === 'fulfilled' && result.value?.suggestions?.length) {
                                allSuggestions = allSuggestions.concat(
                                    result.value.suggestions.filter((spa) => spa.acceptsBooking)
                                );
                            }
                        });
                        const seenSuggestionKeys = new Set();
                        const dedupedSuggestions = allSuggestions.filter((spa) => {
                            const key = this._getSpaKey(spa);
                            if (!key || seenSuggestionKeys.has(key)) return false;
                            seenSuggestionKeys.add(key);
                            return true;
                        });

                        const mapTilerData = mapTilerResult;
                        let suggestionSpasByState = {};
                        let locationFeatures = [];
                        let nearbySpasByState = {};

                        if (dedupedSuggestions.length) suggestionSpasByState = this._groupSpasByState(dedupedSuggestions);
                        if (mapTilerData?.features?.length) locationFeatures = mapTilerData.features;

                        const feature = locationFeatures[0];
                        if (feature?.center?.length) {
                            const [lng, lat] = feature.center;
                            const result = await this._fetchNearbySpasWithRadiusPool(lat, lng, signal);

                            // Re-check focus after the second await as well.
                            if (signal.aborted || document.activeElement !== this._getSearchInput()) return;

                            if (result.spas?.length) {
                                nearbySpasByState = this._groupSpasByState(result.spas);
                            }
                        }

                        const mergedSpasByState = this._mergeSpasByState(suggestionSpasByState, nearbySpasByState);

                        if (Object.keys(mergedSpasByState).length) {
                            this._showSearchDropdown(mergedSpasByState, false, locationFeatures);
                        } else if (locationFeatures.length) {
                            this._showSearchDropdown({}, false, locationFeatures);
                        } else {
                            const dropdown = this._getSearchDropdown();
                            if (dropdown) dropdown.hidden = true;
                        }
                    } catch (error) {
                        if (error.name === 'AbortError') return;
                        const dropdown = this._getSearchDropdown();
                        if (dropdown) dropdown.hidden = true;
                    }
                }

                /**
                 * Search Nearby By Location Query.
                 * @param {string} query - suburb/postcode/address to geocode
                 * @param {Object|null} [spaToSelect] - when set (e.g. a spa suggestion
                 *   was clicked rather than a location suggestion), the matching spa
                 *   card is auto-selected once the full result set has rendered.
                 */
                async _searchNearbyByLocationQuery(query, spaToSelect = null) {
                    // UPDATED — guard now checks _getServiceIds() so the generic
                    // treatment (Zenoti Product List) fallback flow works too.
                    const ids = this._getServiceIds();
                    if (!ids.length) {
                        this._showTabsMessage('Failed to load spas.');
                        return;
                    }
                    this._abortSearch();
                    this._searchController = new AbortController();
                    const {
                        signal
                    } = this._searchController;

                    try {
                        const mapTilerUrl = `${this._mapTilerApiBaseUrl}${encodeURIComponent(query)}.json?key=${this._mapTilerKey}&types=region%2Cmunicipality%2Clocality%2Cneighbourhood%2Cpostal_code%2Caddress&country=au`;
                        const res = await fetch(mapTilerUrl, {
                            signal
                        });
                        const data = await res.json();
                        const feature = data?.features?.[0];
                        if (!feature?.center?.length) {
                            this._showNoSearchResultsInPanels();
                            return;
                        }

                        const [lng, lat] = feature.center;
                        this._fetchNearbySpas(lat, lng, true, spaToSelect);
                    } catch (error) {
                        if (error.name === 'AbortError') return;
                        this._showNoSearchResultsInPanels();
                    }
                }

                /**
                 * Select the spa card in the currently rendered search-results panel
                 * that matches `spaToSelect`, so it ends up with the same is-selected
                 * styling / CTA / summary state as a manual card click.
                 */
                _selectSpaInLocationResults(spaToSelect, spas) {
                    if (!spaToSelect) return;
                    const panel = this.querySelector('#spa-panel-search-results');
                    if (!panel) return;
                    const key = this._getSpaKey(spaToSelect);
                    const index = spas.findIndex((s) => this._getSpaKey(s) === key);
                    const items = panel.querySelectorAll('.spa-item');
                    const matchedItem = index >= 0 ? items[index] : null;
                    if (matchedItem) matchedItem.click();
                }

                /** Fetch Nearby Spas. */
                async _fetchNearbySpas(lat, lng, hideTabs = false, spaToSelect = null) {
                    if (hideTabs) {
                        const panels = this._getTabsPanels();
                        if (panels) {
                            const navScroll = this.querySelector('.spa-tabs-nav-scroll');
                            const tabBorder = this.querySelector('.spa-tab-border');
                            if (navScroll) navScroll.style.display = 'none';
                            if (tabBorder) tabBorder.style.display = 'none';
                        }
                    }

                    this._showLoadingSpinner('Loading spas...');
                    try {
                        const result = await this._fetchNearbySpasWithRadiusPool(lat, lng);
                        const spas = result.spas;
                        const totalSpa = result.totalSpa;
                        if (spas.length) {
                            this._nearMeSpas = spas;
                            if (hideTabs) {
                                this._showLocationSearchResults(spas, totalSpa);
                                this._selectSpaInLocationResults(spaToSelect, spas);
                            } else {
                                this._restoreTabsView();
                                this._renderSpaResults(this._groupSpasByState(spas), true, spas);
                            }
                        } else if (hideTabs) {
                            this._showLocationSearchResults([]);
                        } else {
                            this._showNoNearbySpas();
                        }
                    } catch {
                        if (hideTabs) {
                            this._showLocationSearchResults([]);
                        } else {
                            this._showNoNearbySpas();
                        }
                    }
                }

                /**
                 * Fetch Nearby Spas With Radius Pool.
                 * UPDATED — resolves against _getServiceIds() and, at each radius
                 * tier, fans the lookup out across all IDs and merges + de-dupes
                 * the results (a spa offering more than one of the fallback
                 * treatment IDs is only returned once). Single-ID treatments
                 * behave exactly as before.
                 *
                 * FIXED — all "no results" exits now consistently return
                 * { spas: [], totalSpa: 0 } instead of a bare []. Previously this
                 * returned [] on the empty-service-ids guard, the exhausted-radius
                 * path, and the catch block — callers that destructured
                 * `result.spas` (e.g. _fetchNearbySpas) would then read
                 * `undefined.length` and throw, incorrectly surfacing a genuine
                 * 0-result search as "Failed to load spas."
                 */
                async _fetchNearbySpasWithRadiusPool(lat, lng, signal) {
                    const ids = this._getServiceIds();
                    if (!ids.length) return { spas: [], totalSpa: 0 };
                    const radiusPool = [...new Set([Number(this._defaultDistance) || 50, 100, 1000])];
                    try {
                        for (const distance of radiusPool) {
                            const results = await Promise.all(
                                ids.map((id) => {
                                    const url = `${this._apiBaseUrl}/api/treatments/${id}/spas/nearby?latitude=${lat}&longitude=${lng}&distance=${distance}`;
                                    return fetch(url, signal ? {
                                        signal
                                    } : {}).then((r) => r.json());
                                })
                            );

                            const mergedSpas = [];
                            const seenKeys = new Set();
                            results.forEach((data) => {
                                (data.spas || [])
                                    .filter((spa) => spa.acceptsBooking)
                                    .forEach((spa) => {
                                        const key = this._getSpaKey(spa);
                                        if (!key || seenKeys.has(key)) return;
                                        seenKeys.add(key);
                                        mergedSpas.push(spa);
                                    });
                            });

                            if (mergedSpas.length) {
                                return {
                                    spas: mergedSpas,
                                    totalSpa: mergedSpas.length
                                };
                            }
                        }
                        return { spas: [], totalSpa: 0 };
                    } catch (error) {
                        if (error.name === 'AbortError') throw error;
                        return { spas: [], totalSpa: 0 };
                    }
                }

                /** Group Spas By State. */
                _groupSpasByState(spas) {
                    return spas.reduce((acc, spa) => {
                        const state = spa.state || 'Other';
                        if (!acc[state]) acc[state] = [];
                        acc[state].push(spa);
                        return acc;
                    }, {});
                }
                /** Escape Html. */
                _escapeHtml(value) {
                    const div = document.createElement('div');
                    div.textContent = value == null ? '' : String(value);
                    return div.innerHTML;
                }

                /** Get Spa Suggestion Address. */
                _getSpaSuggestionAddress(spa, includeState = true) {
                    const buildAddress = () => [
                        spa.addressLine1,
                        spa.addressLine2,
                        spa.city,
                        includeState ? spa.state : null,
                        spa.postalCode || spa.postCode
                    ].filter(Boolean).join(', ');

                    const sanitizeFullAddress = (fullAddress) => {
                        if (!fullAddress) return '';
                        if (!spa.country) return fullAddress;
                        const parts = fullAddress.split(',').map((part) => part.trim());
                        const filtered = parts.filter((part, index) => {
                            if (index === parts.length - 1 && part.toLowerCase().includes(spa.country.toLowerCase())) {
                                return false;
                            }
                            return true;
                        });
                        return filtered.join(', ');
                    };

                    if (spa.fullAddress) {
                        const sanitized = sanitizeFullAddress(spa.fullAddress);
                        if (sanitized) return sanitized;
                    }
                    if (spa.address?.fullAddress) {
                        const sanitized = sanitizeFullAddress(spa.address.fullAddress);
                        if (sanitized) return sanitized;
                    }

                    return buildAddress();
                }

                /**
                 * Get Spa Suburb.
                 * Extracts just the suburb/locality name for a spa — e.g. "South
                 * Melbourne" — for use as a search term. Prefers an explicit
                 * city/suburb field; falls back to parsing it out of the full
                 * address string (format is typically
                 * "<street>, <Suburb> <STATE> <postcode>, <Country>").
                 * @param {Object} spa
                 * @returns {string}
                 */
                _getSpaSuburb(spa) {
                    if (spa.city) return spa.city;
                    if (spa.suburb) return spa.suburb;

                    const full = spa.fullAddress || spa.address?.fullAddress || '';
                    if (full) {
                        const parts = full.split(',').map((part) => part.trim()).filter(Boolean);
                        if (spa.country && parts.length && parts[parts.length - 1].toLowerCase().includes(spa.country.toLowerCase())) {
                            parts.pop();
                        }
                        const suburbSegment = parts[parts.length - 1] || '';
                        // Strip a trailing state abbreviation and/or postcode,
                        // e.g. "South Melbourne VIC 3205" -> "South Melbourne"
                        const match = suburbSegment.match(/^(.*?)(?:\s+[A-Z]{2,3})?(?:\s+\d{3,4})?$/);
                        const suburb = (match && match[1].trim()) || suburbSegment;
                        if (suburb) return suburb;
                    }

                    return spa.displayName || '';
                }

                /** Get Location Suggestion Name. */
                _getLocationSuggestionName(feature) {
                    return feature.place_name || feature.text || feature.properties?.name || '';
                }

                /** Render Dropdown Item. */
                _renderDropdownItem({
                    name,
                    address,
                    type,
                    iconType,
                    onSelect,
                    dataAttrs = {}
                }) {
                    const item = document.createElement('div');
                    item.className = `spa-dropdown-item spa-dropdown-item--${iconType || 'spa'}`;
                    item.setAttribute('role', 'button');
                    item.tabIndex = 0;

                    item.innerHTML = `
            <span class="spa-dropdown-item__content">
              <span class="spa-dropdown-name">${this._escapeHtml(name)}</span>
              ${address ? `<span class="spa-dropdown-address">${this._escapeHtml(address)}</span>` : ''}
            </span>
          `;

                    Object.entries(dataAttrs).forEach(([key, value]) => {
                        if (value != null) item.dataset[key] = String(value);
                    });

                    item.addEventListener('click', onSelect);
                    item.addEventListener('keydown', (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onSelect();
                        }
                    });

                    return item;
                }

                /** Show Search Dropdown Loading. */
                _showSearchDropdownLoading() {
                    const dropdown = this._getSearchDropdown();
                    if (!dropdown) return;

                    dropdown.innerHTML = `
            <div class="spa-dropdown-item spa-dropdown-item--loading" role="status">
              <span class="spa-dropdown-item__content">
                <span class="spa-dropdown-name">Loading...</span>
              </span>
            </div>
          `;
                    dropdown.hidden = false;
                }

                /** Show Search Dropdown. */
                _showSearchDropdown(spasByState, showCurrentLocationOnly = false, locationFeatures = []) {
                    const dropdown = this._getSearchDropdown();
                    if (!dropdown) return;

                    dropdown.innerHTML = '';

                    const allSpas = [];

                    Object.values(spasByState || {}).forEach((list) => {
                        if (Array.isArray(list)) allSpas.push(...list);
                    });

                    if (!allSpas.length && !locationFeatures.length && !showCurrentLocationOnly) {
                        dropdown.hidden = true;
                        return;
                    }

                    dropdown.hidden = false;

                    const currentLocationItem = document.createElement('button');
                    currentLocationItem.type = 'button';
                    currentLocationItem.className = 'spa-dropdown-current-location';
                    currentLocationItem.innerHTML = `
            <span class="spa-dropdown-current-location__icon" aria-hidden="true">
              <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.7498 21.45V20.4693C9.66647 20.2551 7.91264 19.4359 6.4883 18.0115C5.06397 16.5872 4.24472 14.8334 4.03055 12.75H3.0498C2.8373 12.75 2.65922 12.6781 2.51555 12.5343C2.37172 12.3905 2.2998 12.2123 2.2998 11.9998C2.2998 11.7871 2.37172 11.609 2.51555 11.4655C2.65922 11.3219 2.8373 11.25 3.0498 11.25H4.03055C4.24472 9.16672 5.06397 7.41288 6.4883 5.98855C7.91264 4.56421 9.66647 3.74496 11.7498 3.5308V2.55005C11.7498 2.33755 11.8217 2.15947 11.9656 2.0158C12.1094 1.87197 12.2876 1.80005 12.5001 1.80005C12.7127 1.80005 12.8908 1.87197 13.0343 2.0158C13.178 2.15947 13.2498 2.33755 13.2498 2.55005V3.5308C15.3331 3.74496 17.087 4.56421 18.5113 5.98855C19.9356 7.41288 20.7549 9.16672 20.9691 11.25H21.9498C22.1623 11.25 22.3404 11.322 22.4841 11.4658C22.6279 11.6096 22.6998 11.7878 22.6998 12.0003C22.6998 12.213 22.6279 12.391 22.4841 12.5345C22.3404 12.6782 22.1623 12.75 21.9498 12.75H20.9691C20.7549 14.8334 19.9356 16.5872 18.5113 18.0115C17.087 19.4359 15.3331 20.2551 13.2498 20.4693V21.45C13.2498 21.6625 13.1779 21.8406 13.0341 21.9843C12.8902 22.1281 12.7121 22.2 12.4996 22.2C12.2869 22.2 12.1088 22.1281 11.9653 21.9843C11.8216 21.8406 11.7498 21.6625 11.7498 21.45ZM12.4998 19C14.4331 19 16.0831 18.3167 17.4498 16.95C18.8165 15.5834 19.4998 13.9334 19.4998 12C19.4998 10.0667 18.8165 8.41672 17.4498 7.05005C16.0831 5.68338 14.4331 5.00005 12.4998 5.00005C10.5665 5.00005 8.91647 5.68338 7.5498 7.05005C6.18314 8.41672 5.4998 10.0667 5.4998 12C5.4998 13.9334 6.18314 15.5834 7.5498 16.95C8.91647 18.3167 10.5665 19 12.4998 19ZM12.4998 15.5C11.5373 15.5 10.7134 15.1573 10.0281 14.4718C9.34255 13.7865 8.9998 12.9625 8.9998 12C8.9998 11.0375 9.34255 10.2136 10.0281 9.5283C10.7134 8.8428 11.5373 8.50005 12.4998 8.50005C13.4623 8.50005 14.2862 8.8428 14.9716 9.5283C15.6571 10.2136 15.9998 11.0375 15.9998 12C15.9998 12.9625 15.6571 13.7865 14.9716 14.4718C14.2862 15.1573 13.4623 15.5 12.4998 15.5ZM12.4998 14C13.0498 14 13.5206 13.8042 13.9123 13.4125C14.304 13.0209 14.4998 12.55 14.4998 12C14.4998 11.45 14.304 10.9792 13.9123 10.5875C13.5206 10.1959 13.0498 10 12.4998 10C11.9498 10 11.479 10.1959 11.0873 10.5875C10.6956 10.9792 10.4998 11.45 10.4998 12C10.4998 12.55 10.6956 13.0209 11.0873 13.4125C11.479 13.8042 11.9498 14 12.4998 14Z" fill="currentColor"></path>
              </svg>
            </span>
            <span class="spa-dropdown-current-location__text">Use my current location</span>
          `;
                    currentLocationItem.addEventListener('mousedown', (event) => event.preventDefault());
                    currentLocationItem.addEventListener('click', () => {
                        dropdown.hidden = true;

                        navigator.permissions.query({
                            name: 'geolocation'
                        }).then((result) => {
                            // Permission already granted → keep existing workflow
                            if (result.state === 'granted') {
                                this._useCurrentLocation();
                                return;
                            }

                            // Trigger browser permission popup
                            navigator.geolocation.getCurrentPosition(
                                () => {
                                    // User allowed access
                                    this._useCurrentLocation();
                                },
                                () => {
                                    // User denied / blocked
                                    this._isCurrentLocationLoading = false;
                                    this._nearMeSpas = 'denied';

                                    const panel = this.querySelector('#spa-panel-NEAR-ME');
                                    if (panel) {
                                        panel.innerHTML = '';
                                        this._showNearMeDisabled(panel);
                                    }
                                }, {
                                    enableHighAccuracy: false,
                                    timeout: 8000,
                                    maximumAge: 300000
                                }
                            );
                        });
                    });
                    dropdown.appendChild(currentLocationItem);

                    allSpas.slice(0, 6).forEach((spa) => {
                        const selectSpa = () => {
                            dropdown.hidden = true;
                            this._clearSelectedSpa();
                            this._disableCta();

                            // Show and search on the spa's suburb (e.g. "South
                            // Melbourne") — not the raw text the user typed, and not
                            // the full street address. This goes through the SAME
                            // nearby-search flow as a manual suburb search (full
                            // result count, distance on every card), just with this
                            // spa pre-selected once the results render.
                            const locationQuery = this._getSpaSuburb(spa);

                            const input = this._getSearchInput();
                            if (input) {
                                input.value = locationQuery;
                                this._setClearButtonVisibility(this._getClearButton(), input);
                            }

                            this._searchNearbyByLocationQuery(locationQuery, spa);
                        };
                        dropdown.appendChild(this._renderDropdownItem({
                            name: spa.displayName || '',
                            address: this._getSpaSuggestionAddress(spa),
                            type: 'Spa',
                            iconType: 'spa',
                            onSelect: selectSpa
                        }));
                    });

                    locationFeatures.slice(0, 5).forEach((feature) => {
                        if (!feature?.center?.length) return;
                        const [lng, lat] = feature.center;

                        const selectLocation = () => {
                            const name = this._getLocationSuggestionName(feature);
                            const input = this._getSearchInput();

                            if (input) {
                                input.value = name;
                                this._setClearButtonVisibility(this._getClearButton(), input);
                            }

                            dropdown.hidden = true;
                            this._disableCta();
                            this._clearSelectedSpa();
                            this._fetchNearbySpas(lat, lng, true);
                        };

                        const locationItem = this._renderDropdownItem({
                            name: this._getLocationSuggestionName(feature),
                            address: feature.properties?.locality || feature.properties?.county || '',
                            type: '',
                            iconType: 'location',
                            onSelect: selectLocation,
                            dataAttrs: {
                                lat,
                                long: lng
                            }
                        });

                        dropdown.appendChild(locationItem);
                    });
                }
                /** Get Spa Key. */
                _getSpaKey(spa) {
                    const rawKey = spa.zenotiGuid || spa.id || spa.handle || `${spa.displayName || ''}-${spa.addressLine1 || spa.city || spa.state || ''}`;
                    return rawKey ? String(rawKey).trim().toLowerCase() : '';
                }

                /** Merge Spas By State. */
                _mergeSpasByState(firstSpasByState = {}, secondSpasByState = {}) {
                    const merged = {};
                    const seen = new Map();
                    const addSpa = (state, spa) => {
                        const key = this._getSpaKey(spa);
                        if (!key) return;
                        const stateKey = state || spa.state || 'Other';

                        const existing = seen.get(key);
                        if (existing) {
                            const existingSpa = existing.spa;
                            if (!existingSpa.distanceFromSearch && spa.distanceFromSearch) {
                                // preserve the existing spa entry but carry over the distance
                                existingSpa.distanceFromSearch = spa.distanceFromSearch;
                            }
                            return;
                        }

                        if (!merged[stateKey]) merged[stateKey] = [];
                        merged[stateKey].push(spa);
                        seen.set(key, {
                            spa,
                            stateKey
                        });
                    };
                    for (const [state, spas] of Object.entries(firstSpasByState)) spas.forEach((spa) => addSpa(state, spa));
                    for (const [state, spas] of Object.entries(secondSpasByState)) spas.forEach((spa) => addSpa(state, spa));
                    return merged;
                }

                /** Show Tabs Message. */
                _showTabsMessage(message) {
                    const nav = this._getTabsNav();
                    const panels = this._getTabsPanels();
                    const legacy = this.querySelector('.booking-modal-spa-results');
                    if (nav) nav.innerHTML = '';
                    if (panels) panels.innerHTML = `<p class="spa-tabs-message">${message}</p>`;
                    else if (legacy) legacy.innerHTML = message;
                }

                // "No nearby spas" — caches empty state then re-renders all tabs so state tabs stay visible.
                // The NEAR ME tab will be active; _loadNearMeTab will call _showNearMeEmpty inside it.
                /** Show No Nearby Spas. */
                _showNoNearbySpas() {
                    this._nearMeSpas = 'empty';
                    this._restoreTabsView();
                    this._renderSpaResults(this._defaultSpaData || {}, false, []);
                }

                /** Show Location Search Results. */
                _showLocationSearchResults(spas, totalSpa) {
                    const navScroll = this.querySelector('.spa-tabs-nav-scroll');
                    const tabBorder = this.querySelector('.spa-tab-border');
                    const panels = this._getTabsPanels();

                    if (navScroll) navScroll.style.display = 'none';
                    if (tabBorder) tabBorder.style.display = 'none';
                    if (!panels) return;

                    panels.innerHTML = '';
                    const searchPanel = document.createElement('div');
                    searchPanel.className = 'spa-tab-panel is-active';
                    searchPanel.id = 'spa-panel-search-results';
                    searchPanel.setAttribute('role', 'tabpanel');

                    if (spas.length) {
                        // Render search results using the full (NEAR ME) card layout
                        searchPanel.innerHTML = `
              <div class="spa-search-result-count">
                  Showing ${totalSpa} location${totalSpa === 1 ? '' : 's'}
              </div>
          `;
                        this._buildSpaItems(spas, searchPanel, {
                            compact: false
                        });
                    } else {
                        this._renderNoResultState(
                            searchPanel,
                            'There are currently no nearby spa locations that offer this treatment',
                            'Find another spa or <button type="button" class="select-diff-treatment-link btn--link">select a different treatment</button>.',
                            this._iconNoTreatment()
                        );
                    }

                    panels.appendChild(searchPanel);
                    this._activeTab = 'SEARCH_RESULTS';
                }

                /** Restore Tabs View. */
                _restoreTabsView() {
                    const navScroll = this.querySelector('.spa-tabs-nav-scroll');
                    const tabBorder = this.querySelector('.spa-tab-border');
                    const nav = this._getTabsNav();
                    if (navScroll) navScroll.style.display = '';
                    if (tabBorder) tabBorder.style.display = '';
                    if (nav) nav.style.display = '';
                }

                /** Build Spa Items. */
                _buildSpaItems(spas, panel, options = {}) {
                    const compact = Boolean(options.compact);
                    // Only matters when compact is false — compact tabs already force state off.
                    const includeState = options.includeState !== false;

                    spas
                    .filter(spa => spa.acceptsBooking)
                    .forEach((spa) => {
                        const item = document.createElement('div');
                        item.className = 'spa-item';
                        const distanceHtml = spa.distanceFromSearch ?
                            `<span class="spa-item__distance">${Number(spa.distanceFromSearch.value).toFixed(1)} ${spa.distanceFromSearch.unit}</span>` :
                            '';

                        const address = this._getSpaSuggestionAddress(spa, compact ? false : includeState);

                        if (compact) {
                            item.innerHTML = `
                                <div class="spa-item__content spa-item__content--compact">
                                <span class="spa-item__name">${spa.displayName || ''}</span>
                                <span class="spa-item__address">${this._escapeHtml(address)}</span>
                                </div>
                                ${distanceHtml ? `<span class="spa-item__distance spa-item__distance--compact">${Number(spa.distanceFromSearch.value).toFixed(1)} ${spa.distanceFromSearch.unit}</span>` : ''}
                            `;
                        } else {
                            item.innerHTML = `
                                <div class="spa-item__content">
                                <span class="spa-item__name">${spa.displayName || ''}</span>
                                <span class="spa-item__address">${this._escapeHtml(address)}</span>
                                </div>
                                ${distanceHtml}
                            `;
                        }

                        const applySelection = () => {
                            this.querySelectorAll('.spa-item.is-selected').forEach((el) => {
                                el.classList.remove('is-selected');
                            });
                            item.classList.add('is-selected');
                            this._selectedSpa = spa;
                            this._isConfirmationStep = false;
                            // UPDATED — this._currentServiceId is '' in the Zenoti
                            // Product List fallback flow, so _patchCtaUrl naturally
                            // omits the serviceid param (no specific treatment
                            // selection has been made yet).
                            this._patchCtaUrl(spa.zenotiGuid, this._currentServiceId);
                            this._enableCta('Continue');
                            this._updateSelectedSpaPanel(spa);
                        };

                        item.addEventListener('click', () => applySelection());

                        panel.appendChild(item);
                    });
                }

                /**
                 * Update Selected Spa Panel.
                 * Writes the spa name into BOTH the desktop right-column panel
                 * AND the mobile footer summary (next to the Continue button) — they
                 * share the same `.js-selected-spa-name` class so a single update
                 * keeps both in sync regardless of which layout is currently visible.
                 */
                _updateSelectedSpaPanel(spa) {
                    const spaPanel = this._getSelectedSpaPanel();
                    const spaPanelMobile = this._getSelectedSpaPanelMobile();
                    const nameEls = this.querySelectorAll('.js-selected-spa-name');
                
                    nameEls.forEach((el) => {
                        el.textContent = `at ${spa.displayName || ''}`;
                    });
                
                    if (spaPanel) {
                        spaPanel.hidden = false;
                    }
                    if (spaPanelMobile) {
                        spaPanelMobile.hidden = false;
                    }
                    this.classList.add('spa-selected'); // NEW — reveals the mobile footer bar
                }

                /** Load Near Me Tab. */
                async _loadNearMeTab(panel) {
                    if (this._isCurrentLocationLoading) return;

                    if (this._nearMeSpas !== null) {
                        panel.innerHTML = '';
                        if (this._nearMeSpas === 'denied') {
                            this._showNearMeDisabled(panel);
                        } else if (this._nearMeSpas === 'empty') {
                            this._showNearMeEmpty(panel);
                        } else if (Array.isArray(this._nearMeSpas) && this._nearMeSpas.length) {
                            this._buildSpaItems(this._nearMeSpas, panel, { includeState: false });
                        } else {
                            this._showNearMeEmpty(panel);
                        }
                        return;
                    }

                    if (!navigator.geolocation) {
                        this._nearMeSpas = 'denied';
                        this._showNearMeDisabled(panel);
                        return;
                    }

                    if (navigator.permissions) {
                        try {
                            const result = await navigator.permissions.query({
                                name: 'geolocation'
                            });
                            if (result.state === 'denied') {
                                // Location access is disabled — show that state directly,
                                // skipping the loading spinner entirely.
                                this._nearMeSpas = 'denied';
                                this._showNearMeDisabled(panel);
                                return;
                            }
                        } catch {
                            /* continue */ }
                    }

                    navigator.geolocation.getCurrentPosition(
                        async (pos) => {
                                this._isCurrentLocationLoading = true;
                                panel.innerHTML = this._spinnerHtml('Getting your location...');

                                const {
                                    latitude: lat,
                                    longitude: lng
                                } = pos.coords;
                                try {
                                    const res = await fetch(`${this._mapTilerApiBaseUrl}${lng},${lat}.json?key=${this._mapTilerKey}&types=region%2Cmunicipality%2Clocality%2Cneighbourhood%2Cpostal_code%2Caddress&country=au`);
                                    const data = await res.json();
                                    const place = data?.features?.[0]?.place_name || null;
                                    const result = await this._fetchNearbySpasWithRadiusPool(lat, lng);
                                    const spas = result.spas;
                                    this._nearMeSpas = spas.length ? spas : 'empty';
                                    this._initialNearMeSpas = this._nearMeSpas;
                                    panel.innerHTML = '';
                                    if (spas.length) this._buildSpaItems(spas, panel, { includeState: false });
                                    else this._showNearMeEmpty(panel);
                                } catch {
                                    this._nearMeSpas = 'empty';
                                    panel.innerHTML = '';
                                    this._showNearMeEmpty(panel);
                                } finally {
                                    this._isCurrentLocationLoading = false;
                                }
                            },
                            () => {
                                this._isCurrentLocationLoading = false;
                                this._nearMeSpas = 'denied';

                                // Show the disabled state immediately
                                panel.innerHTML = '';
                                this._showNearMeDisabled(panel);
                            }, {
                                enableHighAccuracy: false,
                                timeout: 8000,
                                maximumAge: 300000
                            }
                    );
                }

                /**
                 * Show Near Me Empty.
                 * Uses the shared `_renderNoResultState` so the "no nearby spas"
                 * state matches the "no treatment nearby" and "no search results"
                 * states in markup/styling — only the icon and copy differ.
                 */
                _showNearMeEmpty(panel, heading = 'There are currently no nearby spa locations that offer this treatment', sub = 'Find another spa or <button type="button" class="select-diff-treatment-link btn--link">select a different treatment</button>.') {
                    this._renderNoResultState(panel, heading, sub, this._iconNoNearby());
                }

                /** Show No Search Results (no location match in the tabs panels area). */
                _showNoSearchResultsInPanels() {
                    const nav = this._getTabsNav();
                    const panels = this._getTabsPanels();
                    if (nav) nav.innerHTML = '';
                    this._renderNoResultState(
                        panels,
                        "Sorry, we couldn't find any spas for that location.",
                        'Please try a different <button type="button" class="spa-focus-search-link btn--link">suburb, postcode, or address</button>.',
                        this._iconNoSearchResults()
                    );
                }

                /** Show Near Me Disabled. */
                _showNearMeDisabled(panel) {
                    const renderMessage = (heading, body, showRetry = false) => {
                        panel.innerHTML = `
              <div class="near-me-disabled">
                <div class="near-me-disabled__icon">
                <svg
                width="25"
                height="24"
                viewBox="0 0 25 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <mask id="mask0_9624_2063" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="25" height="24">
                  <rect x="0.5" width="24" height="24" fill="#D9D9D9"/>
                </mask>
                <g mask="url(#mask0_9624_2063)">
                  <path d="M11.7498 21.45V20.4693C9.66647 20.2551 7.91264 19.4359 6.4883 18.0115C5.06397 16.5872 4.24472 14.8334 4.03055 12.75H3.0498C2.8373 12.75 2.65922 12.6781 2.51555 12.5343C2.37172 12.3905 2.2998 12.2123 2.2998 11.9998C2.2998 11.7871 2.37172 11.609 2.51555 11.4655C2.65922 11.3219 2.8373 11.25 3.0498 11.25H4.03055C4.24472 9.16672 5.06397 7.41288 6.4883 5.98855C7.91264 4.56421 9.66647 3.74496 11.7498 3.5308V2.55005C11.7498 2.33755 11.8217 2.15947 11.9656 2.0158C12.1094 1.87197 12.2876 1.80005 12.5001 1.80005C12.7127 1.80005 12.8908 1.87197 13.0343 2.0158C13.178 2.15947 13.2498 2.33755 13.2498 2.55005V3.5308C15.3331 3.74496 17.087 4.56421 18.5113 5.98855C19.9356 7.41288 20.7549 9.16672 20.9691 11.25H21.9498C22.1623 11.25 22.3404 11.322 22.4841 11.4658C22.6279 11.6096 22.6998 11.7878 22.6998 12.0003C22.6998 12.213 22.6279 12.391 22.4841 12.5345C22.3404 12.6782 22.1623 12.75 21.9498 12.75H20.9691C20.7549 14.8334 19.9356 16.5872 18.5113 18.0115C17.087 19.4359 15.3331 20.2551 13.2498 20.4693V21.45C13.2498 21.6625 13.1779 21.8406 13.0341 21.9843C12.8902 22.1281 12.7121 22.2 12.4996 22.2C12.2869 22.2 12.1088 22.1281 11.9653 21.9843C11.8216 21.8406 11.7498 21.6625 11.7498 21.45ZM12.4998 19C14.4331 19 16.0831 18.3167 17.4498 16.95C18.8165 15.5834 19.4998 13.9334 19.4998 12C19.4998 10.0667 18.8165 8.41672 17.4498 7.05005C16.0831 5.68338 14.4331 5.00005 12.4998 5.00005C10.5665 5.00005 8.91647 5.68338 7.5498 7.05005C6.18314 8.41672 5.4998 10.0667 5.4998 12C5.4998 13.9334 6.18314 15.5834 7.5498 16.95C8.91647 18.3167 10.5665 19 12.4998 19ZM12.4998 15.5C11.5373 15.5 10.7134 15.1573 10.0281 14.4718C9.34255 13.7865 8.9998 12.9625 8.9998 12C8.9998 11.0375 9.34255 10.2136 10.0281 9.5283C10.7134 8.8428 11.5373 8.50005 12.4998 8.50005C13.4623 8.50005 14.2862 8.8428 14.9716 9.5283C15.6571 10.2136 15.9998 11.0375 15.9998 12C15.9998 12.9625 15.6571 13.7865 14.9716 14.4718C14.2862 15.1573 13.4623 15.5 12.4998 15.5ZM12.4998 14C13.0498 14 13.5206 13.8042 13.9123 13.4125C14.304 13.0209 14.4998 12.55 14.4998 12C14.4998 11.45 14.304 10.9792 13.9123 10.5875C13.5206 10.1959 13.0498 10 12.4998 10C11.9498 10 11.479 10.1959 11.0873 10.5875C10.6956 10.9792 10.4998 11.45 10.4998 12C10.4998 12.55 10.6956 13.0209 11.0873 13.4125C11.479 13.8042 11.9498 14 12.4998 14Z" fill="#2B6229"/>
                </g>
              </svg>
                </div>
                <p class="near-me-disabled__heading">${heading}</p>
                <p class="near-me-disabled__sub">${body}</p>
                ${showRetry ? `<button type="button" class="near-me-disabled__retry btn btn--secondary">Try again</button>` : ''}
              </div>
            `;

                        // "enable location services" link → trigger browser prompt
                        const enableLink = panel.querySelector('.near-me-disabled__link');
                        if (enableLink) {
                            enableLink.addEventListener('click', () => {
                                if (navigator.permissions) {
                                    navigator.permissions.query({
                                        name: 'geolocation'
                                    }).then((result) => {
                                        if (result.state === 'denied') {
                                            this._nearMeSpas = 'denied';
                                            renderMessage(
                                                'Location access is blocked',
                                                'Please enable location access in your browser settings, then refresh and try again. Or <button type="button" class="near-me-disabled__link near-me-disabled__search-link btn--link">search by suburb or postcode</button>.',
                                                false
                                            );
                                            const searchLink = panel.querySelector('.near-me-disabled__search-link');
                                            if (searchLink) searchLink.addEventListener('click', () => {
                                                const input = this._getSearchInput();
                                                if (input) input.focus();
                                            });
                                        } else {
                                            this._nearMeSpas = null;
                                            this._loadNearMeTab(panel);
                                        }
                                    }).catch(() => {
                                        this._nearMeSpas = null;
                                        this._loadNearMeTab(panel);
                                    });
                                } else {
                                    this._nearMeSpas = null;
                                    this._loadNearMeTab(panel);
                                }
                            });
                        }

                        // "search by suburb" link → focus input
                        const searchLink = panel.querySelector('.near-me-disabled__search-link');
                        if (searchLink) {
                            searchLink.addEventListener('click', () => {
                                const input = this._getSearchInput();
                                if (input) input.focus();
                            });
                        }

                        // "Try again" button
                        const retryBtn = panel.querySelector('.near-me-disabled__retry');
                        if (retryBtn) {
                            retryBtn.addEventListener('click', () => {
                                this._nearMeSpas = null;
                                this._loadNearMeTab(panel);
                            });
                        }
                    };

                    renderMessage(
                        "It looks like you haven't enabled location services",
                        'To see nearby spa locations, <button type="button" class="near-me-disabled__link btn--link">enable location services</button>.',
                        false
                    );
                }

                // _renderSpaResults: always renders NEAR ME tab + all default state tabs.
                // spasByState   = results from a search (may be empty/null on initial load)
                // skipNearMe    = true when called from a search (we still add NEAR ME, just set correct active tab)
                // nearbySpas    = flat array of nearby spas resolved during a search (null = not a search call)
                /** Render Spa Results. */
                _renderSpaResults(spasByState, skipNearMe = false, nearbySpas = null) {
                    const nav = this._getTabsNav();
                    const panels = this._getTabsPanels();
                    if (!nav || !panels) {
                        this._renderSpaResultsLegacy(spasByState);
                        return;
                    }
                    this._restoreTabsView();
                    nav.innerHTML = '';
                    panels.innerHTML = '';

                    // Cache nearby spas so the NEAR ME tab can render them immediately
                    if (nearbySpas !== null) {
                        this._nearMeSpas = nearbySpas.length ? nearbySpas : 'empty';
                    }

                    // If we have nearby spas (from a location search), copy distance info
                    // onto other spa entries so distance shows in all tabs when available.
                    let nearbyDistanceMap = null;
                    if (Array.isArray(nearbySpas) && nearbySpas.length) {
                        nearbyDistanceMap = new Map();
                        nearbySpas.forEach((s) => {
                            const key = this._getSpaKey(s);
                            if (key) nearbyDistanceMap.set(key, s.distanceFromSearch);
                        });
                    }

                    // Build the complete ordered list of state tabs to show.
                    // Always use _defaultSpaData as the master state list so no tabs disappear.
                    // For each state, prefer search-result spas if available, otherwise fall back
                    // to the default full list for that state.
                    const defaultStates = this._defaultSpaData ?
                        Object.keys(this._defaultSpaData).filter((s) => this._defaultSpaData[s]?.length) :
                        [];

                    // If we have no data at all, just show NEAR ME
                    if (!defaultStates.length && (!spasByState || !Object.keys(spasByState).length)) {
                        this._buildTab(nav, panels, 'NEAR ME', 'NEAR ME', null, true);
                        this._activeTab = 'NEAR ME';
                        const nearMePanel = panels.querySelector('#spa-panel-NEAR-ME');
                        if (nearMePanel) this._loadNearMeTab(nearMePanel);
                        return;
                    }

                    // Decide which tab to activate:
                    // • After a search with nearby results → NEAR ME
                    // • After a search without nearby results → first state in search results, or first default state
                    // • Initial load → NEAR ME
                    let preferred;
                    if (skipNearMe) {
                        if (nearbySpas && nearbySpas.length) {
                            preferred = 'NEAR ME';
                        } else if (spasByState && Object.keys(spasByState).length) {
                            const searchStates = Object.keys(spasByState).filter((s) => spasByState[s]?.length);
                            preferred = searchStates[0] || defaultStates[0] || 'NEAR ME';
                        } else {
                            preferred = defaultStates[0] || 'NEAR ME';
                        }
                    } else {
                        preferred = (this._activeTab && (this._activeTab === 'NEAR ME' || defaultStates.includes(this._activeTab))) ?
                            this._activeTab :
                            'NEAR ME';
                    }

                    // Always render NEAR ME first
                    this._buildTab(nav, panels, 'NEAR ME', 'NEAR ME', null, preferred === 'NEAR ME');

                    // Render all default state tabs; for each state use search results if present,
                    // otherwise fall back to the full default spa list for that state.
                    defaultStates.forEach((state) => {
                        let spasForTab = (spasByState && spasByState[state] && spasByState[state].length) ?
                            spasByState[state] :
                            this._defaultSpaData[state];
                        // annotate with distanceFromSearch when available
                        if (nearbyDistanceMap) {
                            spasForTab = spasForTab.map((spa) => {
                                const key = this._getSpaKey(spa);
                                const dist = key ? nearbyDistanceMap.get(key) : null;
                                if (dist && !spa.distanceFromSearch) spa.distanceFromSearch = dist;
                                return spa;
                            });
                        }
                        this._buildTab(nav, panels, state, state, spasForTab, state === preferred);
                    });

                    // Also add any extra states that appeared in search results but aren't in defaultStates
                    if (spasByState) {
                        Object.keys(spasByState).forEach((state) => {
                            if (!defaultStates.includes(state) && spasByState[state]?.length) {
                                let extraStateSpas = spasByState[state];
                                if (nearbyDistanceMap) {
                                    extraStateSpas = extraStateSpas.map((spa) => {
                                        const key = this._getSpaKey(spa);
                                        const dist = key ? nearbyDistanceMap.get(key) : null;
                                        if (dist && !spa.distanceFromSearch) spa.distanceFromSearch = dist;
                                        return spa;
                                    });
                                }
                                this._buildTab(nav, panels, state, state, extraStateSpas, state === preferred);
                            }
                        });
                    }

                    this._activeTab = preferred;

                    // If NEAR ME is the active tab, trigger its load
                    if (preferred === 'NEAR ME') {
                        const nearMePanel = panels.querySelector('#spa-panel-NEAR-ME');
                        if (nearMePanel) this._loadNearMeTab(nearMePanel);
                    }
                }

                /** Build Tab. */
                _buildTab(nav, panels, key, label, spas, isActive) {
                    const tabId = `spa-panel-${key.replace(/\W+/g, '-')}`;
                    const btn = document.createElement('button');
                    btn.type = 'button';
                    btn.className = 'spa-tab-btn';
                    btn.textContent = label;
                    btn.dataset.tabKey = key;
                    btn.setAttribute('role', 'tab');
                    btn.setAttribute('aria-selected', String(isActive));
                    btn.setAttribute('aria-controls', tabId);
                    btn.addEventListener('click', () => {
                        nav.querySelectorAll('.spa-tab-btn').forEach((b) => b.setAttribute('aria-selected', 'false'));
                        panels.querySelectorAll('.spa-tab-panel').forEach((p) => p.classList.remove('is-active'));
                        btn.setAttribute('aria-selected', 'true');
                        const panel = panels.querySelector(`#${tabId}`);
                        if (panel) {
                            panel.classList.add('is-active');
                            if (key === 'NEAR ME') this._loadNearMeTab(panel);
                        }
                        this._activeTab = key;
                    });

                    nav.appendChild(btn);
                    const panel = document.createElement('div');
                    panel.className = 'spa-tab-panel' + (isActive ? ' is-active' : '');
                    panel.id = tabId;
                    panel.setAttribute('role', 'tabpanel');
                    if (spas === null) {
                        // NEAR ME tab: render immediately from cache if available, otherwise show the spinner
                        if (key === 'NEAR ME' && this._nearMeSpas !== null && this._nearMeSpas !== 'denied') {
                            if (this._nearMeSpas === 'empty' || (Array.isArray(this._nearMeSpas) && !this._nearMeSpas.length)) {
                                this._showNearMeEmpty(panel);
                            } else if (Array.isArray(this._nearMeSpas) && this._nearMeSpas.length) {
                                this._buildSpaItems(this._nearMeSpas, panel, { includeState: false });
                            }
                        } else {
                            panel.innerHTML = this._spinnerHtml('Getting your location...');
                        }
                    } else {
                        const compactMode = key !== 'NEAR ME';
                        this._buildSpaItems(spas, panel, {
                            compact: compactMode,
                            includeState: key !== 'NEAR ME'
                        });
                    }
                    panels.appendChild(panel);
                }

                /** Render Spa Results Legacy. */
                _renderSpaResultsLegacy(spasByState, expandAll = false) {
                    const container = this.querySelector('.booking-modal-spa-results');
                    if (!container) return;
                    container.innerHTML = '';
                    if (!spasByState || !Object.keys(spasByState).length) return;
                    for (const [state, spas] of Object.entries(spasByState)) {
                        if (!spas?.length) continue;
                        const accordion = document.createElement('details');
                        accordion.className = 'spa-accordion';
                        if (expandAll) accordion.setAttribute('open', 'open');
                        const summary = document.createElement('summary');
                        summary.className = 'spa-accordion-header';
                        summary.innerHTML = `<span>${state}</span><span>${spas.length} spas</span>`;
                        const content = document.createElement('div');
                        spas.forEach((spa) => {
                            const item = document.createElement('div');
                            item.className = 'spa-item';
                            item.innerHTML = `<div>${spa.displayName || ''}</div><div>${spa.addressLine1 || ''}</div>${spa.distanceFromSearch ? `<div>${spa.distanceFromSearch.value} ${spa.distanceFromSearch.unit}</div>` : ''}`;
                            item.addEventListener('click', () => {
                                this._clearSelectedSpa();
                                item.classList.add('is-selected');
                                this._patchCtaUrl(spa.zenotiGuid, this._currentServiceId);
                                this._updateSelectedSpaPanel(spa);
                                this._showRedirectState(spa.displayName || '');
                            });
                            content.appendChild(item);
                        });
                        accordion.appendChild(summary);
                        accordion.appendChild(content);
                        container.appendChild(accordion);
                    }
                }

            }
        );
    });
})();